// @ts-check
// Formulário da pergunta (contrato de bots, seções 4.2 e 5): o texto, o tipo de resposta com a validação de cada um
// (tamanho, faixa, data passada ou futura, padrão), onde guardar (variável do bot e, opcional, um campo do CRM), as
// tentativas, o texto da nova tentativa, a espera e a resposta sensível ("informado" para a equipe).
import { html } from 'htm/preact';
import { Toggle } from '../../../ui/feedback.js';
import { BOT_MESSAGE_LIMITS, buildBotMessagePreview } from '../../../lib/bot-message-limits.js';
import { renderPreviewText } from '../../../lib/bot-test-simulator.js';
import { BOT_ASK_INPUT_KINDS } from '../../../../../shared/bots/bot-definition.js';
import { MAXIMUM_BOT_REGEX_PATTERN_LENGTH } from '../../../../../shared/bots/bot-limits.js';
import { BotMessagePreview } from '../bot-message-preview.js';
import { BotNumberField, BotSaveToField, BotSelectField, BotTextField } from '../bot-form-fields.js';
import { botFieldProblemText } from '../bot-ui.js';
import { t } from '../../../i18n/index.js';

/** @typedef {import('../bot-node-panel.js').BotNodeFormProps} BotNodeFormProps */

const SENSITIVE_BY_DEFAULT = new Set(['CPF', 'DATE']);

/** A resposta é sensível? Padrão verdadeiro em CPF e data (seção 4.2). @param {Record<string, any>} config */
export const isSensitiveAnswer = (config) => config?.isSensitive ?? SENSITIVE_BY_DEFAULT.has(config?.inputKind);

/** @param {BotNodeFormProps} props */
export function AskForm({ node, problems, settings, variables, institutionName, onConfig }) {
  const config = node.config ?? {};
  const validation = config.validation ?? {};
  const kind = config.inputKind ?? 'TEXT';
  /** @param {Record<string, any>} patch */
  const setValidation = (patch) => {
    const next = { ...validation, ...patch };
    const clean = Object.fromEntries(Object.entries(next).filter(([, value]) => value !== null && value !== undefined && value !== '' && value !== false));
    onConfig({ validation: Object.keys(clean).length > 0 ? clean : null });
  };
  const preview = buildBotMessagePreview({ text: config.text }, { renderText: (text) => renderPreviewText(text, { institutionName }) });
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-ask-form">
    <${BotTextField} label=${t('bots.forms.ask.text')} value=${config.text} multiline rows=${3} maximum=${BOT_MESSAGE_LIMITS.textBody} variables=${variables}
      onChange=${(text) => onConfig({ text })} error=${botFieldProblemText(problems, 'text')} testId="bot-ask-text" />
    <${BotSelectField} label=${t('bots.forms.ask.inputKind')} value=${kind} placeholder=${null}
      options=${BOT_ASK_INPUT_KINDS.map((value) => ({ value, label: t(`bots.inputKinds.${value}`) }))}
      onChange=${(inputKind) => onConfig({ inputKind, validation: null, isSensitive: SENSITIVE_BY_DEFAULT.has(String(inputKind)) ? true : config.isSensitive ?? false })}
      hint=${t(`bots.inputKindHints.${kind}`)} />
    ${kind === 'TEXT' || kind === 'NUMBER'
      ? html`<div class="bot-form-row">
          <${BotNumberField} label=${kind === 'TEXT' ? t('bots.forms.ask.minLength') : t('bots.forms.ask.minimum')} value=${validation.min ?? null}
            onChange=${(min) => setValidation({ min })} />
          <${BotNumberField} label=${kind === 'TEXT' ? t('bots.forms.ask.maxLength') : t('bots.forms.ask.maximum')} value=${validation.max ?? null}
            onChange=${(max) => setValidation({ max })} />
        </div>`
      : null}
    ${kind === 'DATE'
      ? html`<${BotSelectField} label=${t('bots.forms.ask.dateRange')} value=${validation.pastOnly ? 'PAST' : validation.futureOnly ? 'FUTURE' : 'ANY'} placeholder=${null}
          options=${['ANY', 'PAST', 'FUTURE'].map((value) => ({ value, label: t(`bots.forms.ask.dateRanges.${value}`) }))}
          onChange=${(value) => setValidation({ pastOnly: value === 'PAST', futureOnly: value === 'FUTURE' })} />`
      : null}
    ${kind === 'REGEX'
      ? html`<${BotTextField} label=${t('bots.forms.ask.pattern')} value=${validation.pattern} maximum=${MAXIMUM_BOT_REGEX_PATTERN_LENGTH}
          onChange=${(pattern) => setValidation({ pattern })} error=${botFieldProblemText(problems, 'validation.pattern')} hint=${t('bots.forms.ask.patternHint')} />`
      : null}
    <${BotSaveToField} value=${config.saveTo} onChange=${(saveTo) => onConfig({ saveTo })} variableError=${botFieldProblemText(problems, 'saveTo.variable')} />
    <label class="flow-checkbox-row bot-toggle-row">
      <${Toggle} value=${isSensitiveAnswer(config)} ariaLabel=${t('bots.forms.ask.sensitive')} onChange=${(/** @type {boolean} */ isSensitive) => onConfig({ isSensitive })} />
      <span>${t('bots.forms.ask.sensitive')}</span>
    </label>
    <p class="flow-field-hint">${t('bots.forms.ask.sensitiveHint')}</p>
    <details class="bot-form-details">
      <summary>${t('bots.forms.ask.retriesTitle')}</summary>
      <${BotTextField} label=${t('bots.forms.ask.retryText')} value=${config.retryText} placeholder=${settings.invalidText ?? ''} variables=${variables}
        onChange=${(retryText) => onConfig({ retryText: retryText || null })} />
      <div class="bot-form-row">
        <${BotNumberField} label=${t('bots.forms.retries')} value=${config.retries} minimum=${0} maximum=${3} placeholder=${String(settings.retries)}
          onChange=${(retries) => onConfig({ retries })} />
        <${BotNumberField} label=${t('bots.forms.timeoutMinutes')} value=${config.timeoutMinutes} minimum=${5} maximum=${1380} placeholder=${String(settings.questionTimeoutMinutes)}
          onChange=${(timeoutMinutes) => onConfig({ timeoutMinutes })} error=${botFieldProblemText(problems, 'timeoutMinutes')} />
      </div>
    </details>
    <div class="bot-preview-box">
      <span class="flow-field-label">${t('bots.preview.title')}</span>
      <div class="bot-chat-surface"><${BotMessagePreview} model=${preview} /></div>
    </div>
  </div>`;
}
